import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Offer } from '../../models/Offer';
import Cart from '../../components/cart/Cart';

type Props = {
  offer: Offer;
};

function OfferCart({ offer }: Props) {
  const [hasValidProducts, setHasValidProducts] = useState<boolean>(false);
  const [mappedProducts, setMappedProducts] = useState<any[]>([]);

  // Check if the offer is available
  const isOfferAvailable =
    offer.validUntil && new Date(offer.validUntil) > new Date();

  useEffect(() => {
    if (!offer || !offer.offerProducts || !Array.isArray(offer.offerProducts)) {
      console.error('Invalid offer products data:', offer);
      return;
    }

    try {
      // Map products in a safer way, with validation and error handling
      const products = offer.offerProducts
        .filter((item) => item && item.product) // Filter out any invalid entries
        .map((offerProduct) => ({
          id: offerProduct.product.id,
          name: offerProduct.product.name || 'Produkt bez nazwy',
          price:
            offerProduct.pricePerProduct || offerProduct.product.price || 0,
          slug: offerProduct.product.slug || '',
          quantity: offerProduct.quantity || 1,
          image:
            offerProduct.product.primaryImage ||
            offerProduct.product.mobilePrimaryImage ||
            '',
          packaged: offerProduct.product.packaged || false,
          deliveryPrice: offer.deliveryPrice || 0,
          deliveryMethod: offer.deliveryMethod || 'STANDARD',
        }));

      // Check if we have valid products
      if (products && products.length > 0) {
        setMappedProducts(products);
        setHasValidProducts(true);
      } else {
        console.error('No valid products found in offer');
        setHasValidProducts(false);
      }
    } catch (error) {
      console.error('Error mapping products:', error);
      setHasValidProducts(false);
    }
  }, [offer]);

  if (!isOfferAvailable) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-red-50 border border-red-100 rounded-lg p-8 text-center my-6"
      >
        <motion.div
          className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: 'spring' }}
        >
          <ErrorOutlineIcon className="text-red-500" style={{ fontSize: 32 }} />
        </motion.div>
        <h3 className="text-xl font-bold text-red-700 mb-2">
          Oferta niedostępna
        </h3>
        <p className="text-red-600 max-w-lg mx-auto">
          Ta oferta nie jest już dostępna lub została usunięta. Prosimy o
          kontakt z obsługą klienta w celu uzyskania szczegółowych informacji.
        </p>
      </motion.div>
    );
  }

  if (!hasValidProducts) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-yellow-50 border border-yellow-100 rounded-lg p-8 text-center my-6"
      >
        <motion.div
          className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: 'spring' }}
        >
          <ErrorOutlineIcon
            className="text-yellow-500"
            style={{ fontSize: 32 }}
          />
        </motion.div>
        <h3 className="text-xl font-bold text-yellow-700 mb-2">
          Brak produktów w ofercie
        </h3>
        <p className="text-yellow-600 max-w-lg mx-auto">
          Nie możemy wyświetlić produktów z tej oferty. Prosimy o kontakt z
          obsługą klienta w celu uzyskania pomocy.
        </p>
      </motion.div>
    );
  }

  return <Cart products={mappedProducts} orderType="offer" offer={offer} />;
}

export default OfferCart;
